import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Subject as SubjectIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

interface EmailTemplatePreviewProps {
  previewData: {
    subject: string;
    content: string;
    variables_used: Record<string, any>;
  };
  onClose: () => void;
}

const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  previewData,
  onClose,
}) => {
  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Email Template Preview</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Subject Preview */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <SubjectIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Subject Line
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="medium">
              {previewData.subject}
            </Typography>
          </Paper>

          {/* Content Preview */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Email Content
            </Typography>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                backgroundColor: '#fafafa',
                minHeight: '300px',
                fontFamily: 'Arial, sans-serif',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: previewData.content }}
            />
          </Paper>

          {/* Variables Used */}
          {previewData.variables_used && Object.keys(previewData.variables_used).length > 0 && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CodeIcon color="action" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  Sample Data Used for Preview
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {Object.entries(previewData.variables_used).map(([key, value]) => (
                  <Box key={key} display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={`$${key}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {typeof value === 'string' ? `&quot;${value}&quot;` : JSON.stringify(value)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Note */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'info.light', color: 'info.contrastText' }}>
            <Typography variant="body2">
              <strong>Note:</strong> This preview uses sample data for variables. 
              The actual email content will vary based on real data when the template is used.
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplatePreview;